import { useCallback } from 'react';
import { useAuth } from '../auth/AuthProvider';
import {
  DRIVE_ERROR_MESSAGES,
  ensureProjectFolder,
  withDriveToken,
  shareFileWithLink,
  uploadFileToDrive,
} from '../files/drive';
import type { DriveFile } from '../files/drive';
import { prepareHuddleScreenshot } from '../huddlePortal/huddleImage';

/**
 * Pasted screenshots for comment threads.
 *
 * Every comment editor in the app shares this one path, so a screenshot
 * behaves the same in a task comment, a project discussion and a supplier
 * thread. Uploads land in a single "TAP Pasted Images" folder rather than the
 * project's own folder: a pasted screenshot is part of a conversation, not a
 * project deliverable, and mixing them would make the project's Files list
 * unreadable within a week.
 *
 * The file is shared by link because the people who need to see it include
 * suppliers outside the TAP Drive. That is also exactly why the sanitizer
 * restricts <img> to Drive hosts — a link-shared image is readable by anyone
 * holding the URL, so the URL must never be attacker-chosen.
 */

export const PASTED_FOLDER_NAME = 'TAP Pasted Images';

/** A clipboard image has no filename of its own; give it a sortable one. */
export function pastedImageName(file: File, now: Date): string {
  const stamp = now.toISOString().replace(/[:.]/g, '-');
  const extension = file.type.split('/')[1] ?? 'png';
  return `pasted-${stamp}.${extension}`;
}

/**
 * Drive's webViewLink opens a viewer page, not the bytes, so it cannot be the
 * src of an <img>. The thumbnail host serves the image itself and is on the
 * sanitizer's allowlist.
 */
export function driveImageSrc(fileId: string): string {
  return `https://lh3.googleusercontent.com/d/${fileId}`;
}

/**
 * Turns a Drive failure code into something the person can act on. This
 * matters most for an expired connection: Google's token is not refreshed by
 * the app, so it dies about an hour after sign-in and no amount of retrying
 * revives it. Saying "try again" there sends someone round a loop that cannot
 * succeed — the only fix is a fresh sign-in.
 */
export function pasteImageErrorMessage(cause: unknown): string {
  const code = cause instanceof Error ? cause.message : '';
  return (
    DRIVE_ERROR_MESSAGES[code] ??
    (code.startsWith('drive_error_')
      ? `Google Drive refused the upload (${code.replace('drive_error_', 'HTTP ')}).`
      : 'That image could not be uploaded. Try again.')
  );
}

/**
 * Screenshot speed (Option A, Kyu 4 Oct): a PNG or JPEG goes through the same
 * 1600 px shrink the Alex Huddle already uses, so a full-size screenshot is
 * not what crosses the network. A GIF keeps its animation and any other type
 * is left alone. A shrink that fails, or does not make the file smaller,
 * uploads the original: slower is better than no picture.
 */
export async function shrinkPastedImage(file: File): Promise<File> {
  if (file.type !== 'image/png' && file.type !== 'image/jpeg') {
    return file;
  }
  try {
    const shrunk = await prepareHuddleScreenshot(file);
    return shrunk.size < file.size ? shrunk : file;
  } catch {
    return file;
  }
}

/**
 * The "TAP Pasted Images" folder id, remembered per user for this tab. Before,
 * every paste searched Drive for the folder first, one extra Google call in
 * line before the upload. Keyed by user because a different sign-in on the
 * same tab has a different Drive.
 */
const pastedFolders = new Map<string, string>();

/** Test seam: start with no remembered folders. */
export function forgetPastedFolders(): void {
  pastedFolders.clear();
}

/**
 * Upload into the pasted folder, using the remembered id when there is one.
 * If the remembered folder was deleted the upload answers 404; the folder is
 * then found (or made) again once and the upload retried. A 404 on a folder
 * just found, or any other failure, is thrown as before.
 */
export async function uploadToPastedFolder(
  token: string,
  userId: string,
  file: File,
): Promise<DriveFile> {
  const remembered = pastedFolders.get(userId);
  const folderId = remembered ?? (await ensureProjectFolder(token, 'root', PASTED_FOLDER_NAME));
  pastedFolders.set(userId, folderId);
  try {
    return await uploadFileToDrive(token, folderId, file);
  } catch (cause) {
    const gone = cause instanceof Error && cause.message === 'drive_error_404';
    if (!gone || remembered === undefined) {
      throw cause;
    }
    pastedFolders.delete(userId);
    const fresh = await ensureProjectFolder(token, 'root', PASTED_FOLDER_NAME);
    pastedFolders.set(userId, fresh);
    return uploadFileToDrive(token, fresh, file);
  }
}

export function usePasteImageUpload(): (file: File) => Promise<string> {
  const { client, access } = useAuth();
  const userId = access.status === 'approved' ? access.profile.id : '';

  return useCallback(
    async (file: File) => {
      try {
        const small = await shrinkPastedImage(file);
        const named = new File([small], pastedImageName(small, new Date()), { type: small.type });
        return await withDriveToken(client, async (token) => {
          const uploaded = await uploadToPastedFolder(token, userId, named);
          await shareFileWithLink(token, uploaded.id);
          return driveImageSrc(uploaded.id);
        });
      } catch (cause) {
        // Re-thrown already readable, so the editor can show it as-is rather
        // than replacing every failure with one generic sentence.
        throw new Error(pasteImageErrorMessage(cause), { cause });
      }
    },
    [client, userId],
  );
}
